import classnames from 'classnames/bind'
import React, { useRef, useContext, ReactNode, forwardRef } from 'react'

import {
  useTimeline,
  useIsomorphicLayoutEffect,
} from '@unlikelystudio/react-hooks'

import { AccordionContext } from '../index'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface AccordionPanelProps {
  className?: string
  index?: number
  restoreIndex?: boolean
  head: ReactNode
  body?: ReactNode | ReactNode[]
  theme?: 'light' | 'dark'
}

const AccordionPanel = forwardRef(
  (
    { className, index, restoreIndex, head, body, theme }: AccordionPanelProps,
    ref: any,
  ) => {
    const { currentIndex, setCurrentIndex } = useContext(AccordionContext)
    const bodyRef = useRef()
    const indexRef = useRef(null)

    const isActive = index === currentIndex

    const tl = useTimeline({ paused: true }, (tl) => {
      if (bodyRef.current) {
        tl.fromTo(
          bodyRef.current,
          { height: 0 },
          {
            height: 'auto',
            duration: 0.7,
            ease: 'expo.inOut',
          },
        )
      }
    })

    const handleClick = (event, index: number) => {
      const newIndex = index === currentIndex ? null : index
      indexRef.current = newIndex
      setCurrentIndex(newIndex)
    }

    useIsomorphicLayoutEffect(() => {
      if (index === currentIndex) tl.play()
      else tl.reverse()
    }, [currentIndex])

    useIsomorphicLayoutEffect(() => {
      if (restoreIndex) setCurrentIndex(null)
    }, [restoreIndex])

    return (
      <div ref={ref} className={cx(className, css.AccordionPanel)}>
        <button
          className={css.head}
          onClick={(event) => handleClick(event, index)}>
          {typeof head === 'function' ? head(isActive) : head}
        </button>
        {body && (
          <div ref={bodyRef} className={css.body}>
            {body}
          </div>
        )}
      </div>
    )
  },
)

export default AccordionPanel
